import { useState, useEffect } from 'react';
import { Star, DollarSign, TrendingUp, Users, Rocket } from 'lucide-react';
import { Modal } from '../ui/Modal';
import ConfettiExplosion from 'react-confetti-explosion';
import type { Product, ProductReviews } from '@smartphone-tycoon/shared-core';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';

interface ProductReleaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
  reviews: ProductReviews;
  onConfirmRelease: () => void;
}

export function ProductReleaseModal({ 
  isOpen, 
  onClose, 
  product, 
  reviews,
  onConfirmRelease 
}: ProductReleaseModalProps) {
  const [showConfetti, setShowConfetti] = useState(false);
  const [hasReleased, setHasReleased] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setShowConfetti(false);
      setHasReleased(false);
    }
  }, [isOpen]);

  if (!product) return null;

  const handleRelease = () => {
    setShowConfetti(true);
    setHasReleased(true);
    // Delay the actual release to allow confetti to show
    setTimeout(() => {
      onConfirmRelease();
    }, 500);
  };

  const handleClose = () => {
    setShowConfetti(false);
    setHasReleased(false);
    onClose();
  };

  const averageRating = [...reviews.professional, ...reviews.customer]
    .reduce((sum, review) => sum + review.rating, 0) / 
    (reviews.professional.length + reviews.customer.length);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Product Release"
        size="xl"
        className="max-h-[90vh] overflow-y-auto"
        data-testid="product-release-modal"
      >
        <div className="space-y-6">
          {/* Header Section */}
          <div className="text-center space-y-4">
            {hasReleased ? (
              <>
                <div className="flex justify-center">
                  <div className="bg-theme-success/10 rounded-full p-4">
                    <Rocket className="w-12 h-12 text-theme-success" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-theme-success">
                  🎉 {product.name} Successfully Released! 🎉
                </h2>
                <p className="text-theme-success">
                  Your product is now available to customers worldwide!
                </p>
              </>
            ) : (
              <>
                <div className="flex justify-center">
                  <div className="bg-theme-info/10 rounded-full p-4">
                    <Rocket className="w-12 h-12 text-theme-info" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-theme-text-primary">
                  Ready to Release {product.name}?
                </h2>
                <p className="text-theme-text-secondary">
                  Review the final specs and early feedback before launching to market.
                </p>
              </>
            )}
          </div>

          {/* Product Stats */}
          <div className="bg-theme-bg-secondary rounded-lg p-6">
            <h3 className="text-lg font-semibold text-theme-text-primary mb-4">Final Product Specifications</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="bg-theme-info/10 rounded-lg p-3">
                  <TrendingUp className="w-6 h-6 text-theme-info mx-auto mb-1" />
                  <div className="text-sm text-theme-info font-medium">Tech Score</div>
                  <div className="text-xl font-bold text-theme-info">{Math.round(product.techScore)}</div>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-theme-success/10 rounded-lg p-3">
                  <Users className="w-6 h-6 text-theme-success mx-auto mb-1" />
                  <div className="text-sm text-theme-success font-medium">Market Appeal</div>
                  <div className="text-xl font-bold text-theme-success">{Math.round(product.marketAppeal)}</div>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-theme-accent/10 rounded-lg p-3">
                  <DollarSign className="w-6 h-6 text-theme-accent mx-auto mb-1" />
                  <div className="text-sm text-theme-accent font-medium">Price</div>
                  <div className="text-xl font-bold text-theme-accent">{formatCurrency(product.price)}</div>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-theme-warning/10 rounded-lg p-3">
                  <Star className="w-6 h-6 text-theme-warning mx-auto mb-1" />
                  <div className="text-sm text-theme-warning font-medium">Early Rating</div>
                  <div className="text-xl font-bold text-theme-warning">{averageRating.toFixed(1)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Early Reviews */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-theme-text-primary">Early Reviews</h3>
            
            {/* Professional Reviews */}
            <div className="bg-theme-info/10 rounded-lg p-4 border border-theme-info/20">
              <h4 className="font-medium text-theme-info mb-3">Professional Reviews</h4>
              <div className="space-y-3">
                {reviews.professional.map((review, index) => (
                  <div key={index} className="border-b border-theme-info/20 pb-2 last:border-b-0">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex text-theme-info">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} className={`w-3 h-3 ${
                            i < review.rating ? 'fill-current' : 'text-theme-text-muted'
                          }`} />
                        ))}
                      </div>
                      <span className="text-sm font-medium text-theme-info">{review.publication}</span>
                      <span className="text-xs text-theme-info">• {review.reviewerTitle}</span>
                    </div>
                    <p className="text-sm text-theme-text-secondary">{review.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Customer Reviews */}
            <div className="bg-theme-bg-secondary rounded-lg p-4">
              <h4 className="font-medium text-theme-text-primary mb-3">Early Customer Feedback</h4>
              <div className="space-y-3">
                {reviews.customer.map((review, index) => (
                  <div key={index} className="border-b border-theme-border pb-2 last:border-b-0">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex text-theme-warning">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} className={`w-3 h-3 ${
                            i < review.rating ? 'fill-current' : 'text-theme-text-muted'
                          }`} />
                        ))}
                      </div>
                      <span className="text-sm font-medium text-theme-text-secondary">{review.author}</span>
                    </div>
                    <p className="text-sm text-theme-text-secondary">{review.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            {hasReleased ? (
              <button
                onClick={() => {
                  setShowConfetti(false);
                  onClose(); // Close the modal when user clicks Continue
                }}
                data-testid="close-celebration"
                className="flex-1 bg-theme-accent text-white py-3 px-6 rounded-lg hover:bg-theme-accent transition-colors font-medium"
              >
                Continue to Market
              </button>
            ) : (
              <>
                <button
                  onClick={onClose}
                  className="flex-1 bg-theme-bg-tertiary text-theme-text-secondary py-3 px-6 rounded-lg hover:bg-theme-bg-secondary transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRelease}
                  data-testid="release-to-market"
                  className="flex-1 bg-theme-accent text-white py-3 px-6 rounded-lg hover:bg-theme-accent transition-colors font-medium flex items-center justify-center gap-2"
                >
                  <Rocket className="w-5 h-5" />
                  Release to Market
                </button>
              </>
            )}
          </div>
        </div>
      </Modal>

      {showConfetti && (
        <>
          {/* Left side confetti */}
          <div style={{ 
            position: 'fixed', 
            left: '20%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 100
          }}>
            <ConfettiExplosion
              force={0.6}
              duration={3000}
              particleCount={150}
              width={800}
              colors={['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD']}
            />
          </div>
          
          {/* Right side confetti */}
          <div style={{ 
            position: 'fixed', 
            right: '20%',
            top: '50%',
            transform: 'translate(50%, -50%)',
            zIndex: 100
          }}>
            <ConfettiExplosion
              force={0.6}
              duration={3000}
              particleCount={150}
              width={800}
              colors={['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD']}
            />
          </div>
        </>
      )}
    </>
  );
}